import { Configuration, Module, ModuleSummaryElement, Project, SeriesData, User } from "../types/type_def";

/**
 * Flows that live in app.ts and that Vue components need to trigger.
 * app.ts registers the implementations at startup. Entries disappear as those flows move to Vue.
 */
export interface LegacyApp {
    /** Login succeeded: store the user and continue to the app (or reset-password page). */
    onLoggedIn(user: User): void;
    /** Login request failed: show the right page again. */
    onLoginFailed(): void;
    /** Header back button */
    onBackClicked(): void;
    /** Header summary button: render the summary images and go to the summary page */
    onSummaryClicked(): void;
    /** User menu "Configurations" */
    onProjectsClicked(): void;
    /** Load a saved configuration by its number and show it in the viewport */
    openConfiguration(identifier: string): Promise<void>;
    /** Start a new (not yet saved) project: continue to series selection */
    startNewProject(project: Project): void;
    /** Series chosen: create the configuration for it. Returns the series (with its presets). */
    selectSeries(seriesId: number): Promise<SeriesData>;
    /** "Configure" chosen: save the project and open the viewport */
    startConfiguring(): Promise<void>;
    /** A material was picked (already stored in the configuration): apply it to the model and save */
    onMaterialSelected(): void;
    /** A product in the product selector was clicked (app.ts may stop the click event) */
    onProductClicked(productNumber: string, event: MouseEvent): void;
    /** What the options modal needs. The configuration and module are the live objects (changed in place). */
    loadOptions(): Promise<OptionsData>;
    /** Options were changed in place on the configuration/entities: update the price and save */
    onOptionsChanged(): Promise<void>;
    /** Renders and downloads the product images of the current configuration */
    renderProductImages(): Promise<void>;
    /** Configuration tile clicked: load that configuration of the project */
    selectConfiguration(id: string): Promise<void>;
    /** Trash icon on a configuration tile: remove it from the project and save */
    deleteConfiguration(id: string): Promise<void>;
    /** "+" tile: start a new configuration in the project */
    newConfiguration(): Promise<void>;
    /** A click outside the 3D view: cancels placing a module */
    onClickOutsideViewport(): void;
    /** Summary page: an accessory amount was changed (recalculates the summary) */
    onAccessoryAmountChanged(productNumber: string, amount: number): void;
    /** Summary page "Order": saves the project and generates the order PDF */
    placeOrder(): Promise<void>;
}

export interface OptionsData {
    series: SeriesData;
    summaryElements: ModuleSummaryElement[];
    configuration: Configuration;
    module: Module;
}

let legacyApp: LegacyApp;

export function registerLegacyApp(app: LegacyApp) {
    legacyApp = app;
}

export function getLegacyApp(): LegacyApp {
    return legacyApp;
}
